import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Phone, MessageCircle, ShieldCheck, Clock, MessageSquare, Store, X } from "lucide-react";

import { DashboardShell } from "@/components/DashboardShell";
import { RequestTimeline } from "@/components/ContactPanel";
import { CUSTOMER_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import {
  cancelContactRequest,
  fetchUnlockedContact,
  listMyContactRequests,
  STATUS_CLASS,
  STATUS_LABEL,
  type ContactRequestStatus,
  type ContactRequestView,
  type UnlockedContact,
} from "@/lib/contact-unlock";

const TABS: readonly (ContactRequestStatus | "all")[] = [
  "all",
  "pending",
  "approved",
  "rejected",
  "expired",
  "cancelled",
];

export const Route = createFileRoute("/_authenticated/customer/contact-unlocks")({
  head: () => ({
    meta: [
      { title: "Contact unlocks · Landoo" },
      {
        name: "description",
        content: "Track your contact requests and view details owners have shared with you.",
      },
    ],
  }),
  component: CustomerContactUnlocksPage,
});

function CustomerContactUnlocksPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<ContactRequestStatus | "all">("all");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["customer", "contact-requests", user?.id],
    queryFn: () => listMyContactRequests(user!.id),
    enabled: !!user,
  });

  const counts = useMemo(() => {
    const acc: Record<string, number> = { all: rows.length };
    for (const s of TABS)
      if (s !== "all") acc[s] = rows.filter((r) => r.effectiveStatus === s).length;
    return acc;
  }, [rows]);

  const visible = useMemo(
    () => (tab === "all" ? rows : rows.filter((r) => r.effectiveStatus === tab)),
    [rows, tab],
  );

  return (
    <DashboardShell title="Contact unlocks" role="Customer" items={CUSTOMER_NAV}>
      <p className="mb-3 text-xs text-muted-foreground">
        Owners approve contact requests one by one. Approved contacts stay visible here for 7 days.
      </p>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {TABS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setTab(s)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
              tab === s
                ? "bg-brown-dark text-primary-foreground"
                : "border border-border bg-card text-brown-dark"
            }`}
          >
            {s === "all" ? "All" : STATUS_LABEL[s]}
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                tab === s ? "bg-white/20" : "bg-secondary text-brown-dark"
              }`}
            >
              {counts[s] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading your contact requests…</p>
      ) : visible.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          You have no {tab === "all" ? "" : STATUS_LABEL[tab as ContactRequestStatus].toLowerCase()}{" "}
          contact requests yet.
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((r) => (
            <CustomerRequestCard key={r.id} row={r} />
          ))}
        </div>
      )}
    </DashboardShell>
  );
}

function CustomerRequestCard({ row }: { row: ContactRequestView }) {
  const [unlocked, setUnlocked] = useState<UnlockedContact | null>(null);
  const qc = useQueryClient();
  const { user } = useAuth();

  const cancel = useMutation({
    mutationFn: () => cancelContactRequest(row.id),
    onSuccess: () => {
      toast.success("Request withdrawn.");
      qc.invalidateQueries({ queryKey: ["customer", "contact-requests", user?.id] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Could not cancel request"),
  });

  useEffect(() => {
    if (row.effectiveStatus !== "approved") return setUnlocked(null);
    fetchUnlockedContact(row.id)
      .then(setUnlocked)
      .catch(() => setUnlocked(null));
  }, [row.id, row.effectiveStatus]);

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span
          className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${STATUS_CLASS[row.effectiveStatus]}`}
        >
          {STATUS_LABEL[row.effectiveStatus]}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {new Date(row.created_at).toLocaleDateString()}
        </span>
      </div>
      <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Store className="h-3.5 w-3.5" /> {row.counterpartyName ?? "Landoo Partner"}
      </div>
      <div className="text-sm font-semibold text-brown-dark">{row.listingTitle ?? "Listing"}</div>

      {row.message && (
        <div className="mt-2 flex items-start gap-2 rounded-xl bg-secondary/60 p-2.5 text-xs text-brown-dark">
          <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span>{row.message}</span>
        </div>
      )}
      {row.response_note && (
        <p className="mt-1 text-[11px] text-muted-foreground">Owner note: {row.response_note}</p>
      )}

      {row.effectiveStatus === "approved" && (
        <div className="mt-2 space-y-1.5 rounded-xl bg-cream/60 p-2.5">
          <div className="flex items-center gap-1 text-[11px] font-semibold text-rent">
            <ShieldCheck className="h-3.5 w-3.5" /> Contact shared with you
          </div>
          {unlocked?.phone && (
            <a
              href={`tel:${unlocked.phone}`}
              className="flex items-center gap-2 rounded-xl bg-secondary/60 px-3 py-2 text-sm font-semibold text-brown-dark"
            >
              <Phone className="h-4 w-4" /> {unlocked.phone}
            </a>
          )}
          {unlocked?.whatsapp && (
            <a
              href={`https://wa.me/${unlocked.whatsapp.replace(/[^0-9]/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded-xl bg-secondary/60 px-3 py-2 text-sm font-semibold text-brown-dark"
            >
              <MessageCircle className="h-4 w-4" /> {unlocked.whatsapp}
            </a>
          )}
          {!unlocked?.phone && !unlocked?.whatsapp && (
            <p className="text-xs text-muted-foreground">
              Owner approved but hasn't added contact numbers yet.
            </p>
          )}
          {row.expires_at && (
            <div className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Clock className="h-3 w-3" /> Access ends{" "}
              {new Date(row.expires_at).toLocaleDateString()}
            </div>
          )}
        </div>
      )}

      <RequestTimeline row={row} />

      <div className="mt-3 flex flex-wrap gap-2">
        <Link
          to="/property/$id"
          params={{ id: row.listing_id }}
          className="rounded-full bg-secondary px-3 py-1.5 text-xs font-semibold text-brown-dark"
        >
          View listing
        </Link>
        {row.effectiveStatus === "pending" && (
          <button
            type="button"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate()}
            className="inline-flex items-center gap-1 rounded-full border border-coral/40 px-3 py-1.5 text-xs font-semibold text-coral disabled:opacity-60"
          >
            <X className="h-3.5 w-3.5" /> {cancel.isPending ? "Cancelling…" : "Cancel request"}
          </button>
        )}
      </div>
    </div>
  );
}
